import type { UserProfile } from '@elara/client-core/models/UserProfileModel';

import { useRef } from 'react';

import { fileToAsset } from '@elara/client-core/lib/asset';
import { cn } from '@elara/client-core/lib/utils';

import { useToastState, useUserProfileState } from '#states/hooks.js';
import { Avatar, AvatarFallback, AvatarImage } from '#ui/index.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

/**
 * The Account header avatar, made editable (ELARA-518). The whole affordance lives on the avatar:
 * clicking it (camera hint on hover) opens the native file chooser and uploads the picked image via
 * the shared {@link useUserProfileState} `uploadAvatar` action. Whenever there's a displayed photo,
 * hovering reveals a delete icon button in the bottom-right corner that clears it back to initials.
 * Feedback is a toast; the avatar updating reactively is the visible success signal.
 */
export function EditableAvatar({ profile }: { profile: UserProfile }) {
  const profileState = useUserProfileState();
  const toastState = useToastState();
  const inputRef = useRef<HTMLInputElement>(null);

  const isBusy = profileState.isUploadingAvatar;
  // The remove control is offered whenever there's a displayed photo (custom upload or provider
  // photo) — keyed off photoURL, not hasCustomAvatar, so it doesn't depend on that flag having
  // round-tripped through the server.
  const hasPhoto = !!profile.photoURL;

  const openPicker = () => inputRef.current?.click();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset the input so picking the same file again still fires `change`.
    event.target.value = '';
    if (!file) return;
    await profileState.uploadAvatar(fileToAsset(file));
    if (profileState.avatarError) {
      toastState.addToast({ title: profileState.avatarError.message, variant: 'destructive' });
    } else {
      toastState.addToast({ title: 'Photo updated', variant: 'success' });
    }
  };

  const handleRemove = async () => {
    await profileState.removeAvatar();
    if (profileState.avatarError) {
      toastState.addToast({ title: profileState.avatarError.message, variant: 'destructive' });
    } else {
      toastState.addToast({ title: 'Photo removed', variant: 'success' });
    }
  };

  return (
    <div className="flex items-center gap-4 mb-2 mt-5">
      <div className="group relative">
        <button
          type="button"
          onClick={openPicker}
          disabled={isBusy}
          aria-label="Change profile photo"
          className="relative block rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed"
        >
          <Avatar className="h-16 w-16 border">
            {/* Hide any current photo/initials while uploading so only the spinner shows. */}
            {!isBusy && <AvatarImage src={profile.photoURL || undefined} />}
            <AvatarFallback className="text-xl">{isBusy ? '' : profile.initials || ''}</AvatarFallback>
          </Avatar>
          <span
            className={cn(
              'absolute inset-0 flex items-center justify-center rounded-full text-white transition-opacity',
              // While uploading, keep a solid overlay + white spinner visible regardless of hover so
              // it's obvious something is happening; otherwise it's just a hover/focus affordance.
              isBusy
                ? 'bg-black/60 opacity-100'
                : 'bg-black/45 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
            )}
          >
            {isBusy ? (
              <MaterialIcon icon="progress_activity" size={IconSizes.md} className="animate-spin" />
            ) : (
              <MaterialIcon icon="photo_camera" size={IconSizes.sm} />
            )}
          </span>
        </button>
        {hasPhoto && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={isBusy}
            aria-label="Remove profile photo"
            className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border bg-destructive text-white shadow-sm transition-opacity focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed md:opacity-0 md:group-hover:opacity-100"
          >
            <MaterialIcon icon="delete" size={IconSizes.xs} />
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
          data-testid="avatar-file-input"
        />
      </div>
      <div>
        <h1 className="text-2xl font-display font-light">{profile.displayName}</h1>
        <p className="text-sm text-muted-foreground">{profile.email}</p>
      </div>
    </div>
  );
}
